/**
 * RequireOrganization Guard
 * 
 * Production-grade organization gating:
 * - Redirects to /onboarding/workspace if no organization exists
 * - Prevents modal overlay issues on protected routes
 * - Only triggers workspace creation in dedicated flow
 */
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import logger from '../../utils/logger';

const RequireOrganization = ({ children }) => {
  const location = useLocation();
  const [status, setStatus] = useState('loading'); // 'loading' | 'has-org' | 'no-org'

  useEffect(() => {
    checkOrganization();
  }, []);

  const checkOrganization = async () => {
    try {
      // Check localStorage first for faster response
      const cachedOrgId = localStorage.getItem('currentOrganization');
      
      if (cachedOrgId) {
        // Verify the org still exists and user has access
        try {
          await api.get(`/organizations/${cachedOrgId}`);
          setStatus('has-org');
          return;
        } catch (error) {
          // Org might have been deleted or user lost access
          localStorage.removeItem('currentOrganization');
        }
      }

      // Fetch all user's organizations
      const response = await api.get('/organizations');
      const orgs = response.data;

      if (orgs && orgs.length > 0) {
        // User has at least one organization
        const defaultOrg = orgs.find(o => o.lastAccessedAt) || orgs[0];
        localStorage.setItem('currentOrganization', defaultOrg._id);
        setStatus('has-org');
      } else {
        // No organizations - user needs to create one
        setStatus('no-org');
      }
    } catch (error) {
      logger.error('Failed to check organization status', error);
      // On error, assume no org to be safe
      setStatus('no-org');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading workspace...</p>
        </div>
      </div>
    );
  }

  if (status === 'no-org') {
    // Redirect to dedicated workspace creation page
    // Preserve original destination for redirect after creation
    return (
      <Navigate 
        to="/onboarding/workspace" 
        state={{ from: location.pathname }}
        replace 
      />
    );
  }

  return children;
};

export default RequireOrganization;
